import { Button, Checkbox, Modal, Typography, message } from "antd";
import { PARTNER_TERMS_URL } from "Constants/common";
import { useUserInfo } from "GlobalStates/User";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Constants/AllRoutes";
import apiClient from "QueryClient/axiosClient";
import { partnerTermsMetadata, partnerTermsText } from "./partnerTermsContent";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { getPartnerTermsStatus } from "./apis/getPartnerTermsStatus";

const { Paragraph, Link } = Typography;

const getSessionId = () => {
  const key = "onegradSessionId";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const next =
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  sessionStorage.setItem(key, next);
  return next;
};

const hashText = async (text) => {
  const data = new TextEncoder().encode(text);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

export const TermsConsentModal = () => {
  const { user, setUser } = useUserInfo();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  console.log("Rendering TermsConsentModal with user:", user);
  const { data: partnerTermsStatus, refetch } = useQuery(
    ["partnerTermsStatus", user?.token, partnerTermsMetadata.policyVersion],
    () => getPartnerTermsStatus(user?.token),
    {
      enabled: !!user?.token,
      retry: false,
      staleTime: 0,
    },
  );

  useEffect(() => {
    if (!user?.token) {
      setIsOpen(false);
      return;
    }

    if (partnerTermsStatus?.accepted === false) {
      setIsOpen(true);
      setIsChecked(false);
      return;
    }
    if (partnerTermsStatus?.accepted === true) {
      setIsOpen(false);
    }
  }, [user?.token, partnerTermsStatus]);

  useEffect(() => {
    if (user?.token) {
      refetch();
    }
  }, [user?.token, refetch]);

  const handleNext = async () => {
    try {
      setIsSubmitting(true);
      const documentHash = await hashText(partnerTermsText);
      await apiClient.post("/policyAcceptances", {
        subjectType: "B2B_PARTNER",
        policyKey: partnerTermsMetadata.policyKey,
        policyVersion: partnerTermsMetadata.policyVersion,
        effectiveDate: partnerTermsMetadata.effectiveDate,
        documentUrl: partnerTermsMetadata.documentUrl,
        documentHash,
        acceptanceMethod: "CLICKWRAP_MODAL",
        locale: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        appVersion: process.env.REACT_APP_APP_VERSION,
        sessionId: getSessionId(),
      });
      await queryClient.invalidateQueries({
        queryKey: ["partnerTermsStatus"],
      });
      setIsOpen(false);
    } catch (error) {
      message.error("Unable to save your acceptance. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setUser({
      token: "",
      name: "",
      role: "",
      hasCompletedProfile: false,
      isVerified: false,
      picture: "",
    });
    navigate(ROUTES.HOME, { state: { justLoggedOut: true } });
  };

  return (
    <Modal
      open={isOpen}
      footer={
        <>
          <Button onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleNext}
            disabled={!isChecked || isSubmitting}
            loading={isSubmitting}
          >
            Agree & Continue
          </Button>
        </>
      }
      closable={false}
      maskClosable={false}
      centered
      destroyOnClose
      title="OneGrad Partner Terms"
    >
      <Paragraph>
        Before you can access the Partner Dashboard, you must review and accept
        the OneGrad Partner Terms.
      </Paragraph>
      <Paragraph>
        <strong>Effective Date:</strong>{" "}
        {partnerTermsMetadata.effectiveDateDisplay}
        <br />
        <strong>Version:</strong> {partnerTermsMetadata.policyVersion}
      </Paragraph>
      <Paragraph>
        Read the full document{" "}
        <Link href={PARTNER_TERMS_URL} target="_blank" rel="noreferrer">
          Partner Terms
        </Link>{" "}
        (opens in a new tab).
      </Paragraph>
      <Checkbox
        checked={isChecked}
        onChange={(event) => setIsChecked(event.target.checked)}
      >
        I have read and agree to the OneGrad Partner Terms, including the
        independent contractor, confidentiality, payment, and dispute resolution
        provisions.
      </Checkbox>
    </Modal>
  );
};
